import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import AutosizeInput from '../../../../../AutosizeInput';
import { colors } from '../../../../../../config/styles';
import { zodFieldErrors } from '../../../../../../utils/zod';
import { ZodEffects, ZodObject, ZodString, ZodTypeAny } from 'zod';
import KubitTooltip from '../../../../../KubitTooltip';
import { SvgIcon } from '../../../../../Icon';
import { EditableLabelSize } from './types';
import { theme } from '../../../../../../styles/theme';

interface IProps {
  value: string;
  validation?: ZodObject<
    { value: ZodEffects<ZodString, string, string> },
    'strip',
    ZodTypeAny,
    { value: string },
    { value: string }
  >;
  placeholder?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  size?: EditableLabelSize;
  fontColor?: string;
}

const InputContainer = styled.div`
  position: relative;
  height: 24px;
`;

const Input = styled(AutosizeInput)<{
  $hasError: boolean;
  $size: EditableLabelSize;
  $fontColor?: string;
}>`
  &,
  input {
    min-width: 64px;
    align-items: flex-start;
    font-size: 16px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: normal;
    color: ${theme.colors.secondary900};

    ${({ $size }) =>
      $size === 'small' &&
      `
      font-size: 12px;
      font-weight: 600;
    `};

    ${({ $size }) =>
      $size === 'large' &&
      `
      font-size: 24px;
      font-weight: 600;
    `};

    ${({ $fontColor }) =>
      $fontColor &&
      `
      color: ${$fontColor};
    `};
  }

  &::after,
  input {
    padding: 1px 3px;
    padding-right: 16px;
    border-radius: 2px;
    border: 1px solid
      ${({ $hasError }) =>
        $hasError ? colors.mainError : theme.colors.mainBlue};
  }
`;

const ErrorIcon = styled(SvgIcon)`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
`;

export default ({
  value,
  validation,
  placeholder = '',
  onSubmit,
  onCancel,
  size = 'medium',
  fontColor,
}: IProps) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!hasError) {
      onSubmit(event.target.value);
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    if (event.key === 'Enter') {
      target.blur();
    }

    if (event.key === 'Escape') {
      onCancel();
    }
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;

    if (validation) {
      const safeParse = validation.safeParse({
        value: target.value,
      });

      if (safeParse.success) {
        setHasError(false);
        setErrorMessage(undefined);
      } else {
        const error = zodFieldErrors(safeParse.error, 'value');
        if (error?.length) {
          setHasError(true);
          setErrorMessage(error);
          target.focus();
        }
      }
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.select();
    }
  }, []);

  return (
    <InputContainer>
      <Input
        ref={inputRef}
        type="text"
        onBlur={handleBlur}
        onKeyUp={handleKeyUp}
        onInput={handleInput}
        defaultValue={value}
        autoFocus
        $hasError={hasError}
        $size={size}
        $fontColor={fontColor}
        placeholder={placeholder}
      />
      {hasError && (
        <KubitTooltip content={errorMessage} noOpenDelay={true}>
          <ErrorIcon name="error-icon" width={12} height={12} />
        </KubitTooltip>
      )}
    </InputContainer>
  );
};
